import React, {useEffect, useState} from 'react';
import TextInput from '../../uiToolkit/Inputs/TextInput';
import ImagePickerInput from '../../uiToolkit/Inputs/ImagePickerInput';
import {orgDataFormText} from './orgDataFormText';
import {OrganizationSettings} from '../../data/models/Oganization/Organization';
import {resizeImage} from '../../utils/resizeImage';
import './styles.css';
import Dropdown from '../../uiToolkit/Inputs/Dropdown';
import Switch from '../../uiToolkit/Inputs/Switch';
import {Icon} from '../../uiToolkit/Icon/Icon';
import Button from '../../uiToolkit/Buttons/Button';
import {HexColorPicker} from 'react-colorful';
import {LLM_PROVIDER_OPTIONS, ORGANIZATION_TYPE_OPTIONS} from './options';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
} from '../../data/staticValues/colors';
import {toast} from 'react-toastify';
import {appText} from '../../assets/text/appText';
import {toastConfig} from '../../uiToolkit/Toast/toastConfig';

const ORG_AVATAR_WIDTH = 300;
const ORG_AVATAR_HEIGHT = 300;
const HEX_COLOR = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

// A color the workspace saved, which older values may store without the '#', or the default.
const savedColor = (value: string | undefined, fallback: string) => {
  const color = value?.trim();
  const hex = color && !color.startsWith('#') ? `#${color}` : color;
  return hex && HEX_COLOR.test(hex) ? hex : fallback;
};

const sameColor = (shown: string, saved: string) =>
  shown.trim().toLowerCase() === saved.toLowerCase();

interface Props {
  onSubmit: (
    name: string,
    // Undefined unless the user changed them, so a save never overwrites what they did not touch.
    primaryColor: string | undefined,
    secondaryColor: string | undefined,
    type: number,
    llmProvider?: string,
    image?: File,
    hideCourseIconsInApp?: boolean,
  ) => void;
  onCancel?: () => void;
  submitText: string;
  isLoading: boolean;
  organization?: OrganizationSettings;
  skipColors?: boolean;
  skipLlm?: boolean;
  skipLearnerAppSettings?: boolean;
  customNameTitle?: string;
  customNameSubtitle?: string;
  customImageTitle?: string;
  customTypeTitle?: string;
  customTypeSubtitle?: string;
  customOrgTypeSubtitle?: string;
}

const OrgDataForm = React.memo(
  ({
    onSubmit,
    submitText,
    onCancel,
    isLoading,
    organization,
    skipColors,
    customImageTitle,
    customNameSubtitle,
    customNameTitle,
    customTypeSubtitle,
    customTypeTitle,
    skipLlm,
    skipLearnerAppSettings,
    customOrgTypeSubtitle,
  }: Props) => {
    const [name, setName] = useState('');
    const [image, setImage] = useState<File>();
    // The workspace's own colors, not this browser's theme cache, which can be empty, stale or another workspace's.
    const [initialPrimaryColor] = useState(() =>
      savedColor(organization?.primaryColor, DEFAULT_PRIMARY_COLOR),
    );
    const [initialSecondaryColor] = useState(() =>
      savedColor(organization?.secondaryColor, DEFAULT_SECONDARY_COLOR),
    );
    const [primaryColor, setPrimaryColor] = useState(initialPrimaryColor);
    const [secondaryColor, setSecondaryColor] = useState(initialSecondaryColor);
    const [type, setType] = useState('');
    const [llmProvider, setLlmProvider] = useState(LLM_PROVIDER_OPTIONS[0].key);
    const [hideCourseIcons, setHideCourseIcons] = useState(false);

    const [dropdownOpen, setDropdownOpen] = useState(false);

    // COLOR PICKER
    const [primaryColorPick, setPrimaryColorPick] = useState(false);
    const [secondaryColorPick, setSecondaryColorPick] = useState(false);

    useEffect(() => {
      if (organization) {
        setName(organization.name);
        setType(organization.type.toString());
        if (organization.llmProvider) {
          setLlmProvider(organization.llmProvider);
        }
        setHideCourseIcons(!!organization.hideCourseIconsInApp);
      }
    }, []);

    const onChangeImage = async (file?: File) => {
      if (file) {
        const compressed = await resizeImage(
          file,
          ORG_AVATAR_WIDTH,
          ORG_AVATAR_HEIGHT,
        );
        setImage(compressed);
      } else {
        setImage(undefined);
      }
    };

    const primaryHandleColorPickerOn = () => {
      setPrimaryColorPick(true);
    };

    const secondaryHandleColorPickerOn = () => {
      setSecondaryColorPick(true);
    };

    const primaryHandleColorPickerOff = () => {
      setPrimaryColorPick(false);
    };

    const secondaryHandleColorPickerOff = () => {
      setSecondaryColorPick(false);
    };

    const onResetPrimaryColor = () => {
      setPrimaryColor(DEFAULT_PRIMARY_COLOR);
    };

    const onResetSecondaryColor = () => {
      setSecondaryColor(DEFAULT_SECONDARY_COLOR);
    };

    const validatePrimaryColor = () => {
      if (!HEX_COLOR.test(primaryColor)) {
        setPrimaryColor(initialPrimaryColor);
        toast.error(appText.invalidHEXValue, toastConfig);
      }
    };

    const validateSecondaryColor = () => {
      if (!HEX_COLOR.test(secondaryColor)) {
        setSecondaryColor(initialSecondaryColor);
        toast.error(appText.invalidHEXValue, toastConfig);
      }
    };

    const colorsChanged =
      !skipColors &&
      (!sameColor(primaryColor, initialPrimaryColor) ||
        !sameColor(secondaryColor, initialSecondaryColor));

    const submitEnabled = () => {
      if (organization) {
        // Only what the form shows can change.
        return (
          (name !== organization.name ||
            (!skipLlm && llmProvider !== organization.llmProvider) ||
            colorsChanged ||
            (!skipLearnerAppSettings &&
              hideCourseIcons !== !!organization.hideCourseIconsInApp) ||
            image) &&
          name?.trim() !== '' &&
          (skipColors ||
            (primaryColor?.trim() !== '' && secondaryColor?.trim() !== ''))
        );
      }
      return (
        name?.trim() !== '' &&
        primaryColor?.trim() !== '' &&
        secondaryColor?.trim() !== '' &&
        image &&
        type !== ''
      );
    };

    const onCreate = () => {
      if (submitEnabled()) {
        // The workspace stores the colors as a pair, so a change to either sends both.
        onSubmit(
          name.trim(),
          colorsChanged ? primaryColor.trim() : undefined,
          colorsChanged ? secondaryColor.trim() : undefined,
          parseInt(type, 10),
          skipLlm ? undefined : llmProvider,
          image,
          skipLearnerAppSettings ? undefined : hideCourseIcons,
        );
      }
    };

    return (
      <div className="OrgDataForm">
        <TextInput
          value={name}
          onChange={setName}
          placeholder={orgDataFormText.namePlaceholder}
          heading={
            customNameTitle ? customNameTitle : orgDataFormText.nameTitle
          }
          subtitle={customNameSubtitle}
          className="Input"
          maxCharacters={40}
          isMandatory={true}
        />
        <ImagePickerInput
          image={image}
          onChange={onChangeImage}
          heading={
            customImageTitle ? customImageTitle : orgDataFormText.avatarTitle
          }
          placeholder={orgDataFormText.avatarPlaceholder}
          className="Input"
          existingImage={organization?.avatar}
          existingImageName={organization?.avatarName}
          isMandatory={true}
        />
        {/* <TextInput
          value={description}
          onChange={setDescription}
          placeholder={orgDataFormText.desscriptionPlaceholder}
          heading={orgDataFormText.description}
          className="Input"
          isMandatory={true}
        /> */}
        {!skipColors && (
          <>
            <div className="ColorTextInputContainer">
              <TextInput
                value={primaryColor}
                onChange={setPrimaryColor}
                heading={orgDataFormText.primaryColorTitle}
                subtitle={customTypeSubtitle}
                onBlurFunc={validatePrimaryColor}
                placeholder={orgDataFormText.primaryColorPlaceholder}
                className="Input"
                isMandatory={true}
                onFocusFunc={primaryHandleColorPickerOn}
              />
              <Button
                title={orgDataFormText.reset}
                onClick={onResetPrimaryColor}
                className="ColorResetButton"
              />
            </div>
            {primaryColorPick && (
              <div className="colorPickContainer">
                <span
                  className="colorPickClose icon-type-bolder_x"
                  onClick={primaryHandleColorPickerOff}></span>
                <HexColorPicker
                  color={primaryColor}
                  onChange={setPrimaryColor}
                />
              </div>
            )}
            <div className="ColorTextInputContainer">
              <TextInput
                value={secondaryColor}
                onChange={setSecondaryColor}
                heading={orgDataFormText.secondaryColorTitle}
                placeholder={orgDataFormText.secondaryColorPlaceholder}
                className="Input"
                isMandatory={true}
                onFocusFunc={secondaryHandleColorPickerOn}
                onBlurFunc={validateSecondaryColor}
              />
              <Button
                title={orgDataFormText.reset}
                onClick={onResetSecondaryColor}
                className="ColorResetButton"
              />
            </div>
            {secondaryColorPick && (
              <div className="colorPickContainer">
                <span
                  className="colorPickClose icon-type-bolder_x"
                  onClick={secondaryHandleColorPickerOff}></span>
                <HexColorPicker
                  color={secondaryColor}
                  onChange={setSecondaryColor}
                />
              </div>
            )}
          </>
        )}
        <Dropdown
          disabled={organization !== undefined}
          options={ORGANIZATION_TYPE_OPTIONS}
          selectedOption={type}
          placeholder={orgDataFormText.selectType}
          setSelectedOption={setType}
          heading={customTypeTitle ? customTypeTitle : orgDataFormText.type}
          subtitle={customOrgTypeSubtitle}
          isMandatory={organization === undefined}
          onDropdownVisibilityToggle={setDropdownOpen}
        />
        {!skipLlm && (
          <Dropdown
            options={LLM_PROVIDER_OPTIONS}
            selectedOption={llmProvider}
            placeholder={orgDataFormText.llmProviderPlaceholder}
            setSelectedOption={setLlmProvider}
            heading={orgDataFormText.selectLLMProvider}
            onDropdownVisibilityToggle={setDropdownOpen}
            className="mt-3"
          />
        )}
        {!skipLearnerAppSettings && (
          <div className="LearnerAppSection">
            <p className="LearnerAppSectionTitle">
              {orgDataFormText.learnerAppSettings}
            </p>
            <div className="LearnerAppSetting">
              <div className="LearnerAppSettingInfo">
                <p className="LearnerAppSettingTitle">
                  {orgDataFormText.courseIconsTitle}
                </p>
                <p className="LearnerAppSettingSubtitle">
                  {orgDataFormText.courseIconsSubtitle}
                </p>
                <div className="LearnerAppSettingHint">
                  <Icon
                    name="editor_info_ic.svg"
                    className="LearnerAppSettingHintIcon"
                  />
                  <span>{orgDataFormText.courseIconsHint}</span>
                </div>
              </div>
              <div className="LearnerAppSettingControl">
                <Switch
                  value={!hideCourseIcons}
                  disabled={false}
                  onToggle={() => setHideCourseIcons(!hideCourseIcons)}
                />
              </div>
            </div>
          </div>
        )}

        <div className="Footer">
          {onCancel ? (
            <Button
              title={orgDataFormText.cancel}
              onClick={onCancel}
              uiType="text"
            />
          ) : (
            <div />
          )}

          <Button
            title={submitText}
            onClick={onCreate}
            disabled={!submitEnabled()}
            isLoading={isLoading}
            uiType="action"
          />
        </div>
        <div className={dropdownOpen ? 'ScrollPadding' : ''} />
      </div>
    );
  },
);

export default OrgDataForm;
